import { DEI, DEI_EE_MFRS } from '../../../constants/qnames';
import { FilingFormState } from '../filing-form-state.service';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { DimensionVisibilityConfig } from './dimension-visibility.util';

/**
 * NATURE — the Consolidated-dimension gate descriptor.
 * Reads the DEI field "Nature of Financial Statements"
 * (`ssmt-dei-ee-mfrs:NatureOfFinancialStatements`). The Consolidated dimension
 * renders & validates only when this equals 'Consolidated'; 'Separate' hides
 * it; an unknown/unset value defaults to shown (pre-subscription posture).
 */
const NATURE = {
  field: DEI_EE_MFRS.NatureOfFinancialStatements,
  consolidatedValue: 'Consolidated',
} as const;

/**
 * PREP — the PreviousYear (comparative/period) gate descriptor.
 * Reads the DEI field "Financial Statements Preparation for Current Submission"
 * (`ssmt-dei:DisclosureOfFinancialStatementsPreparationForCurrentSubmission`).
 * A *first-time* preparation has NO comparative, so PreviousYear is hidden; any
 * other value (subsequent, or unknown) shows PreviousYear.
 */
const PREP = {
  field: DEI.DisclosureOfFinancialStatementsPreparationForCurrentSubmission,
  firstTimeValue: 'First time preparation of financial statements after incorporation',
} as const;

/** Central registry of dimension-gated sheets. Add a newly-gated sheet here. */
export const DIMENSION_VISIBILITY_CONFIGS: Partial<
  Record<keyof FilingFormState, DimensionVisibilityConfig>
> = {
  [SHEET_KEYS.statementOfFinancialPosition]: { consolidatedGate: NATURE, periodGate: PREP },
  [SHEET_KEYS.statementOfProfitOrLoss]: { consolidatedGate: NATURE, periodGate: PREP },
  [SHEET_KEYS.analysisOfProfitOrLoss]: { consolidatedGate: NATURE, periodGate: PREP },
  [SHEET_KEYS.statementOfComprehensiveIncome]: { consolidatedGate: NATURE, periodGate: PREP },
  [SHEET_KEYS.statementOfChangesInEquity]: { consolidatedGate: NATURE, periodGate: PREP },
  [SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity]: { consolidatedGate: NATURE, periodGate: PREP },
  [SHEET_KEYS.statementOfCashFlowsIndirectMethod]: { consolidatedGate: NATURE, periodGate: PREP },
  [SHEET_KEYS.notesIssuedCapital]: { consolidatedGate: NATURE, periodGate: null },
  [SHEET_KEYS.notesRelatedPartyTransactions]: { consolidatedGate: NATURE, periodGate: null },
};

export function getDimensionVisibilityConfig(sheetKey: keyof FilingFormState): DimensionVisibilityConfig | undefined {
  return DIMENSION_VISIBILITY_CONFIGS[sheetKey];
}
